/**
 * CategoryCombobox - An editable dropdown for product categories
 * Fetches existing categories from Shopify and allows selecting or entering new ones
 */
import React, { useState, useEffect, useRef } from 'react';
import { shopifyService } from '../services/shopifyService';

interface CategoryComboboxProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
}

// Default pottery categories as fallback
const DEFAULT_CATEGORIES = [
  'Keramik',
  'Pottery',
  'Vase',
  'Schale',
  'Teller',
  'Tasse',
  'Skulptur',
  'Dekoration',
  'Geschirr',
  'Unikat'
];

export const CategoryCombobox: React.FC<CategoryComboboxProps> = ({
  value,
  onChange,
  placeholder = 'Kategorie wählen oder eingeben',
  className = '',
  id,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState<string[]>(DEFAULT_CATEGORIES);
  const [filteredCategories, setFilteredCategories] = useState<string[]>(DEFAULT_CATEGORIES);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<globalThis.HTMLInputElement>(null);
  const dropdownRef = useRef<globalThis.HTMLDivElement>(null);
  const containerRef = useRef<globalThis.HTMLDivElement>(null);

  // Fetch categories from Shopify stats
  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoading(true);
      try {
        const statsResponse = await shopifyService.getStats();
        if (statsResponse.stats?.categories) {
          // Extract category names and sort by usage count
          const categoryNames = Object.entries(statsResponse.stats.categories)
            .sort((a, b) => b[1] - a[1])
            .map(([name]) => name)
            .filter(name => name && name !== 'Uncategorized');
          
          if (categoryNames.length > 0) {
            // Merge with defaults, keeping Shopify categories first
            const merged = Array.from(new Set([...categoryNames, ...DEFAULT_CATEGORIES]));
            setCategories(merged);
            setFilteredCategories(merged);
          }
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        // Keep default categories on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Filter categories based on input
  useEffect(() => {
    if (!value) {
      setFilteredCategories(categories);
    } else {
      const lowerValue = value.toLowerCase();
      const filtered = categories.filter(cat => 
        cat.toLowerCase().includes(lowerValue)
      );
      setFilteredCategories(filtered);
    }
    setHighlightedIndex(-1);
  }, [value, categories]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as globalThis.Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleSelectCategory = (category: string) => {
    onChange(category);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredCategories.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredCategories.length) {
          handleSelectCategory(filteredCategories[highlightedIndex]);
        } else {
          setIsOpen(false);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const items = dropdownRef.current.querySelectorAll('[data-category-item]');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const baseInputClass = "block w-full border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 sm:text-sm pr-8";

  const listboxId = id ? `${id}-listbox` : 'category-listbox';

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`${baseInputClass} ${className}`}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className="absolute inset-y-0 right-0 flex items-center pr-2"
          tabIndex={-1}
          aria-label="Toggle dropdown"
        >
          {isLoading ? (
            <svg className="animate-spin h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <svg className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </button>
      </div>

      {isOpen && filteredCategories.length > 0 && (
        <div
          ref={dropdownRef}
          id={listboxId}
          className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
          role="listbox"
        >
          {filteredCategories.map((category, index) => (
            <div
              key={category}
              data-category-item
              onClick={() => handleSelectCategory(category)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-3 py-2 cursor-pointer text-sm ${
                index === highlightedIndex
                  ? 'bg-clay-100 text-clay-900'
                  : value.toLowerCase() === category.toLowerCase()
                  ? 'bg-clay-50 text-clay-800'
                  : 'text-gray-900 hover:bg-gray-50'
              }`}
              role="option"
              aria-selected={value.toLowerCase() === category.toLowerCase()}
            >
              {category}
              {value.toLowerCase() === category.toLowerCase() && (
                <svg className="inline-block ml-2 h-4 w-4 text-clay-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          ))}
        </div>
      )}

      {isOpen && filteredCategories.length === 0 && value && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg p-3 text-sm text-gray-500">
          Keine bestehenden Kategorien gefunden. &ldquo;<span className="font-medium text-gray-700">{value}</span>&rdquo; wird als neue Kategorie verwendet.
        </div>
      )}
    </div>
  );
};

export default CategoryCombobox;
